import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Avatar } from '../../../components/avatar'
import { Button } from '../../../components/button'
import CountCard from '../../../components/cards/CountCard'
import Icon from '../../../components/icon'
import { DateFilterNav } from '../../../components/inputs/dateFilterNav'
import Dropdown from '../../../components/inputs/dropdown'
import { SearchInput } from '../../../components/inputs/searchInput'
import ModalComponent from '../../../components/modal'
import { AddAffiliateModal } from '../../../components/modal/app_modals/affiliates/addAffiliateModal'
import { ManageCollectionModal } from '../../../components/modal/app_modals/affiliates/manageCollectionModal'
import { ConfirmationModal } from '../../../components/modal/app_modals/confirmationModal'
import { TableComponent } from '../../../components/table'
import { CountCardContainer } from '../../../components/wrapper/CountCardsContainer'
import { COLLECTION_WEBSITE_OPTIONS } from '../../../constants'
import { formatCurrency } from '../../../helpers'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  useDeleteAffiliateMutation,
  useGetAffiliatesMetricsQuery,
  useGetAffiliatesQuery,
  useLazyGetAffiliateCollectionQuery,
} from '../../../redux-toolkits/loyalty/loyalty.slice'
import { ReportPeriodRange } from '../../../redux-toolkits/reports/reports.type'
import { QueryProps } from '../../../redux-toolkits/shared/shared.type'
import { buildCollectionUrl } from './collectionUrl'

const COLLECTION_URL_COPIED_MESSAGE = 'Collection URL copied to clipboard'
const COLLECTION_URL_CONFIG_ERROR = 'Collection URL is not configured'
const COLLECTION_URL_COPY_ERROR = 'Unable to copy collection URL'
const COPY_COLLECTION_URL_MODAL = 'copyCollectionUrlModal'

type CollectionCopyAffiliate = {
  affiliateId: string
  affiliateSlug: string
}

type CollectionCopyWebsite = {
  name: string
  value: string
}

export const Affiliates = () => {
  const [query, setQuery] = useState<QueryProps>()
  const [selectedAffiliateId, setSelectedAffiliateId] = useState<string>()
  const [selectedCollectionCopyAffiliate, setSelectedCollectionCopyAffiliate] =
    useState<CollectionCopyAffiliate>()
  const [copyingCollectionUrl, setCopyingCollectionUrl] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()

  const { modal, modalOpen, setModalOpen, handleModalClose, handleModalOpen } =
    useModalControl()

  const {
    data: affiliates,
    isFetching: fetchingAffiliates,
    isError,
  } = useGetAffiliatesQuery(
    query
      ? {
          ...query,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const {
    data: affiliatesMetrics,
    isFetching: fetchingAffiliatesMetrics,
    refetch: refetchAffiliatesMetrics,
  } = useGetAffiliatesMetricsQuery(
    query
      ? {
          startDate: query.startDate,
          endDate: query.endDate,
          filter: query.filter,
          isCustomDateRange: query.isCustomDateRange,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const [deleteAffiliate, { isLoading: deletingAffiliate }] =
    useDeleteAffiliateMutation()
  const [getAffiliateCollection] = useLazyGetAffiliateCollectionQuery()

  const handleCopyCollectionUrl = async (website: CollectionCopyWebsite) => {
    if (!selectedCollectionCopyAffiliate || !website.value) {
      toast.error(COLLECTION_URL_CONFIG_ERROR)
      return
    }

    setCopyingCollectionUrl(true)

    let lat: string | undefined
    let lng: string | undefined

    try {
      const collection = await getAffiliateCollection({
        affiliateId: selectedCollectionCopyAffiliate.affiliateId,
      }).unwrap()

      lat = collection.data?.latitude
      lng = collection.data?.longitude
    } catch {
      lat = undefined
      lng = undefined
    }

    try {
      const url = buildCollectionUrl({
        baseUrl: website.value,
        affiliateSlug: selectedCollectionCopyAffiliate.affiliateSlug,
        lat,
        lng,
      })

      await navigator.clipboard.writeText(url)
      toast.success(COLLECTION_URL_COPIED_MESSAGE)
      setSelectedCollectionCopyAffiliate(undefined)
      handleModalClose()
    } catch {
      toast.error(COLLECTION_URL_COPY_ERROR)
    } finally {
      setCopyingCollectionUrl(false)
    }
  }

  useEffect(() => {
    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setQuery({
      page: Number(searchParams.get('page') || 1),
      limit: 20,
      search: searchParams.get('search') || undefined,
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
      isCustomDateRange: custDate,
      filter: custDate
        ? undefined
        : (searchParams.get('filter') as ReportPeriodRange) ||
          'inception_to_date',
    })
  }, [searchParams])

  return (
    <>
      <div className="flex flex-col gap-10">
        <DateFilterNav
          filter={query?.filter ?? 'custom'}
          isCustomDateRange={query?.isCustomDateRange ?? false}
          startDate={query?.startDate}
          endDate={query?.endDate}
          setFilter={(filter) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              filter: filter.filter ?? '',
              isCustomDateRange: String(filter.isCustomDateRange),
              endDate: filter.endDate ?? '',
              startDate: filter.startDate ?? '',
              skip: '0',
            })
          }}
        />

        <CountCardContainer className="!my-0">
          <CountCard
            count={affiliatesMetrics?.data?.totalAffiliates ?? 0}
            text="Total affiliates"
            isCurrency={false}
            loading={fetchingAffiliatesMetrics}
            valueSuffix={
              (affiliatesMetrics?.data?.totalAffiliates ?? 0) === 1
                ? 'Affiliate'
                : 'Affiliates'
            }
          />
          <CountCard
            count={affiliatesMetrics?.data?.totalAffiliateLists}
            text="Total affiliate lists"
            isCurrency={false}
            loading={fetchingAffiliatesMetrics}
          />
          <CountCard
            count={affiliatesMetrics?.data?.totalRevenueGenerated}
            text="Total revenue generated"
            isCurrency={true}
            loading={fetchingAffiliatesMetrics}
          />
          <CountCard
            count={affiliatesMetrics?.data?.totalPayoutToAffiliates}
            text="Total payout to affiliates"
            isCurrency={true}
            loading={fetchingAffiliatesMetrics}
          />
          {/* <CountCard
            count={affiliatesMetrics?.data?.totalSignups}
            text="Total signups"
            isCurrency={false}
            loading={fetchingAffiliatesMetrics}
          />
          <CountCard
            count={affiliatesMetrics?.data?.totalFirstPurchases}
            text="Total first purchases"
            isCurrency={false}
            loading={fetchingAffiliatesMetrics}
          /> */}
        </CountCardContainer>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="!w-full md:!max-w-[400px]">
            <SearchInput
              searchFunction={(search) => {
                setSearchParams((prev) => {
                  const current = Object.fromEntries(prev)

                  return {
                    ...current,
                    search,
                    page: '1',
                  }
                })
              }}
              placeholder="Search"
              initialValue={searchParams.get('search') || undefined}
            />
          </div>

          <div className="flex items-center gap-2">
            <Button
              label={`Add affiliate`}
              primary={true}
              className="!font-normal ml-auto"
              onClick={() => {
                setSelectedAffiliateId(undefined)
                handleModalOpen('addAffiliateModal')
              }}
            />
          </div>
        </div>

        <div className="w-full overflow-x-scroll">
          <TableComponent
            name="Affiliates"
            showName={false}
            headers={[
              'AFFILIATES',
              // 'TOTAL SIGNUPS',
              // 'TOTAL FIRST PURCHASES',
              'TOTAL REVENUE',
              'AMOUNT EARNED',
              'ACTIONS',
            ]}
            rows={
              affiliates?.data?.docs?.map((item) => {
                return {
                  id: item.affiliateId,
                  content: [
                    <div
                      key={`${item.affiliateId}-affiliate-nae`}
                      className="flex gap-2 items-center"
                    >
                      <Avatar name={item.affiliateName} useRandomBgColor />

                      <span className="!capitalize">{item.affiliateName}</span>
                    </div>,
                    // item.affiliateSignups?.toLocaleString(),
                    // item.affiliateFirstPurchases?.toLocaleString(),
                    formatCurrency(item.totalRevenueGenerated, {
                      currency: 'NGN',
                      decimalPlaces: 2,
                    }),
                    formatCurrency(item.affiliateEarnings, {
                      currency: 'NGN',
                      decimalPlaces: 2,
                    }),
                    <Dropdown
                      key={`${item.affiliateId}-controls`}
                      menuButton={
                        <Icon
                          id="ellipses"
                          height={18}
                          width={18}
                          className=""
                        />
                      }
                      menuItems={[
                        {
                          name: 'Edit',

                          value: '',
                          onClick: () => {
                            setSelectedAffiliateId(item.affiliateId)
                            handleModalOpen('editAffiliateModal')
                          },
                        },
                        {
                          name: 'Manage collection',
                          value: '',
                          onClick: () => {
                            setSelectedAffiliateId(item.affiliateId)
                            handleModalOpen('manageCollectionModal')
                          },
                        },
                        {
                          name: 'Copy collection URL',
                          value: '',
                          onClick: () => {
                            setSelectedCollectionCopyAffiliate({
                              affiliateId: item.affiliateId,
                              affiliateSlug: item.affiliateSlug,
                            })
                            handleModalOpen(COPY_COLLECTION_URL_MODAL)
                          },
                        },
                        {
                          name: 'Delete affiliate',
                          value: '',
                          onClick: () => {
                            setSelectedAffiliateId(item.affiliateId)
                            handleModalOpen('deleteConfirmationModal')
                          },
                          className: '!text-danger',
                        },
                      ]}
                    />,
                  ],
                }
              }) ?? []
            }
            onPageChange={(page) => {
              setSearchParams((prev) => {
                const current = Object.fromEntries(prev)

                return {
                  ...current,
                  page: `${page}`,
                }
              })
            }}
            pageLimit={Number(query?.limit ?? 0)}
            currentPage={Number(query?.page ?? 0)}
            loading={fetchingAffiliates}
            isEmpty={
              isError || [undefined, 0].includes(affiliates?.data?.docs?.length)
            }
            totalDataCount={affiliates?.data.total ?? 0}
          />
        </div>
      </div>

      <AddAffiliateModal
        open={
          (modal === 'addAffiliateModal' || modal === 'editAffiliateModal') &&
          modalOpen
        }
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
          refetchAffiliatesMetrics()
        }}
        affiliateId={selectedAffiliateId}
      />

      <ManageCollectionModal
        open={modal === 'manageCollectionModal' && modalOpen}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
        }}
        affiliateId={selectedAffiliateId}
      />

      <ConfirmationModal
        open={modal === 'deleteConfirmationModal' && modalOpen}
        setOpen={setModalOpen}
        message="Are you sure you want to delete this affiliate customer?"
        title="Delete this affiliate customer"
        onCloseCallback={() => {
          setSelectedAffiliateId(undefined)
          handleModalClose()
        }}
        onConfirm={async () => {
          const resp = await deleteAffiliate({
            id: selectedAffiliateId!,
          })

          if (resp.data?.success) {
            toast.success(resp.data.message)
            handleModalClose()
          }
        }}
        loading={deletingAffiliate}
        confirmText="Delete"
      />

      <ModalComponent
        open={modal === COPY_COLLECTION_URL_MODAL && modalOpen}
        setOpen={setModalOpen}
        title="Copy collection URL"
        onCloseCallback={() => {
          setSelectedCollectionCopyAffiliate(undefined)
          handleModalClose()
        }}
        childrenClassName="md:!max-w-[420px]"
      >
        <div className="flex flex-col gap-4">
          <p className="text-base text-sec-black">
            Select the website to attach to this collection URL.
          </p>

          <div className="flex flex-col gap-3">
            {COLLECTION_WEBSITE_OPTIONS.map((website) => (
              <Button
                key={website.name}
                label={website.name}
                type="button"
                variant="border-grey"
                className="w-full !justify-between"
                disabled={copyingCollectionUrl}
                loading={copyingCollectionUrl}
                onClick={() => {
                  void handleCopyCollectionUrl(website)
                }}
              />
            ))}
          </div>
        </div>
      </ModalComponent>
    </>
  )
}
